import { Refresh as RefreshIcon } from '@mui/icons-material';
import {
  Box,
  Button,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

export interface BulkEditProgressRow {
  id: string;
  name: string;
  itemNumber: string;
  status: 'pending' | 'in-progress' | 'success' | 'fail';
  error?: string;
}

export interface BulkEditProgressProps {
  rows: BulkEditProgressRow[];
  onRetry: (itemId: string) => void;
  onClose: () => void;
}

export const BulkEditProgress = ({ rows, onRetry, onClose }: BulkEditProgressProps) => {
  const { t } = useTranslation();

  const total = rows.length;
  const successCount = rows.filter((r) => r.status === 'success').length;
  const allSuccess = total > 0 && successCount === total;

  const progressValue = total === 0 ? 0 : (successCount / total) * 100;

  return (
    <Box>
      <Typography variant="subtitle1" gutterBottom>
        {t('items.bulkEdit.progress.title')}
      </Typography>

      <Box sx={{ mb: 2 }}>
        <LinearProgress variant="determinate" value={progressValue} />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {t('items.bulkEdit.progress.completedOf', {
            completed: successCount,
            total,
          })}
        </Typography>
      </Box>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('items.bulkEdit.progress.column.name')}</TableCell>
              <TableCell>{t('items.bulkEdit.progress.column.number')}</TableCell>
              <TableCell>{t('items.bulkEdit.progress.column.status')}</TableCell>
              <TableCell>{t('items.bulkEdit.progress.column.error')}</TableCell>
              <TableCell>{t('items.bulkEdit.progress.column.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id}>
                <TableCell>{row.name}</TableCell>
                <TableCell>{row.itemNumber}</TableCell>
                <TableCell>
                  <Typography
                    variant="body2"
                    data-testid={`row-status-${row.id}`}
                    color={
                      row.status === 'success'
                        ? 'success.main'
                        : row.status === 'fail'
                          ? 'error.main'
                          : 'text.secondary'
                    }
                  >
                    {t(`items.bulkEdit.progress.status.${row.status}`)}
                  </Typography>
                </TableCell>
                <TableCell>
                  {row.status === 'fail' && row.error ? (
                    <Typography variant="body2" color="error">
                      {row.error}
                    </Typography>
                  ) : null}
                </TableCell>
                <TableCell>
                  {row.status === 'fail' ? (
                    <IconButton
                      size="small"
                      data-testid={`retry-button-${row.id}`}
                      onClick={() => {
                        onRetry(row.id);
                      }}
                      aria-label={t('items.bulkEdit.progress.retry')}
                    >
                      <RefreshIcon fontSize="small" />
                    </IconButton>
                  ) : null}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 2 }}>
        <Button
          data-testid="close-button"
          variant="contained"
          disabled={!allSuccess}
          onClick={onClose}
        >
          {t('items.bulkEdit.progress.close')}
        </Button>
      </Box>
    </Box>
  );
};
